import {
  Badge,
  Box,
  CircularProgress,
  Dialog,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Typography,
  useTheme,
} from "@mui/material";

import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { Availability } from "app/components/Business/types";
import { useCallback, useMemo } from "react";
import { dateAndTimeStyles as styles } from "styles/Checkout/dateAndTime.styles";
import { ReactComponent as CustomDropDownIcon } from "../../../assets/icons/black_down_arrow.svg";

// Styled Badge for marking dots
const CustomBadge = styled(Badge)(({ theme, invisible }) => ({
  "& .MuiBadge-dot": {
    bottom: "0px",
    left: "50%",
    transform: "translateX(-50%)",
    display: invisible ? "none" : "block",
  },
}));

interface DateAndTimeProps {
  itemType: number;
  selectedDate: dayjs.Dayjs | null;
  handleDateChange: (newDate: Dayjs | null) => void;
  isCalendarVisible: boolean;
  setIsCalendarVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTime: string;
  setSelectedTime: (time: string) => void;
  availableTimes: string[];
  availabilities?: Availability[];
  unit_name?: string;
  price_description?: string | undefined;
  ticket?: any | undefined;
  basePrice?: any | undefined;
  price?: number;
  setPrice?: (value: number) => void;
  selectedTicket: number;
  setSelectedTicket: (value: number) => void;
  availability?: any;
  onMonthChange?: (date: Dayjs) => void;
  isLoadingAvailabilities?: boolean;
}

const DateAndTime: React.FC<DateAndTimeProps> = ({
  selectedDate,
  handleDateChange,
  isCalendarVisible,
  setIsCalendarVisible,
  selectedTime,
  setSelectedTime,
  availableTimes,
  availabilities,
  unit_name,
  price_description,
  ticket,
  basePrice,
  price,
  setPrice,
  selectedTicket,
  setSelectedTicket,
  availability,
  onMonthChange,
  isLoadingAvailabilities,
}) => {
  const theme = useTheme();

  const quantityOptions = useMemo(
    () => Array.from({ length: ticket }, (_, index) => index + 1),
    [ticket]
  );

  const handleTicketChange = useCallback(
    (event: SelectChangeEvent) => {
      if (price && setPrice) {
        let newPrice = price - selectedTicket * basePrice;
        setSelectedTicket(Number(event.target.value));
        newPrice += Number(event.target.value) * basePrice;
        setPrice(newPrice);
      }
    },
    [price, selectedTicket, ticket, setPrice, basePrice]
  );

  // Custom Day component with a badge for marked dates
  const CustomDay = (props: any) => {
    const { day, outsideCurrentMonth, ...rest } = props;

    const formattedDate = dayjs(day).format("YYYY-MM-DD");
    const isToday = dayjs().isSame(day, "day");

    if (outsideCurrentMonth) {
      return (
        <PickersDay
          day={day}
          {...rest}
          outsideCurrentMonth={outsideCurrentMonth}
        />
      );
    }

    const isMarked = availabilities?.some(
      (availability) =>
        dayjs(availability.start_time).format("YYYY-MM-DD") === formattedDate
    );

    return (
      <CustomBadge
        overlap="circular"
        variant="dot"
        invisible={!isMarked}
        color="success"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <PickersDay
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          {...rest}
          sx={styles.customDay(isToday)}
        />
      </CustomBadge>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {!availability?.private && (
        <Typography sx={styles.checkAvailabilityText(theme)}>
          Check Availability
        </Typography>
      )}
      <Box sx={styles.calendarBox}>
        {/* Date Picker and Calendar Modal */}
        <Box
          sx={styles.datePickerBox}
          onClick={() => setIsCalendarVisible(true)}
        >
          <Typography sx={styles.dateText(theme)}>
            {selectedDate?.format("ddd, MMM DD")}
          </Typography>
          <CustomDropDownIcon style={styles.dropdownIcon} />
        </Box>

        {/* Time Picker */}
        <Box sx={styles.timePickerBox}>
          <FormControl variant="outlined" sx={styles.formControl}>
            <Select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              displayEmpty
              IconComponent={CustomDropDownIcon}
              disabled={availableTimes?.length === 0}
              renderValue={(selected) =>
                availableTimes.length > 0 ? selected || "Select Time" : "N/A"
              }
              sx={styles.selectInputTime}
            >
              {availableTimes.map((qty) => (
                <MenuItem key={qty} value={qty.toString()}>
                  {qty}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Ticket Box */}
      <Box sx={styles.ticketBox(availability)}>
        <Typography sx={styles.ticketHeading}>Let’s get started!</Typography>
        <FormControl fullWidth variant="outlined" sx={styles.ticketFormControl}>
          <Box sx={styles.ticketSelectBox}>
            <Box>
              <Typography sx={styles.unitNameText}>{unit_name}</Typography>
              <Typography sx={styles.availableText}>
                {ticket} Available
              </Typography>
            </Box>

            <Typography sx={styles.priceText}>${basePrice}</Typography>
          </Box>
          <FormControl variant="outlined" sx={styles.ticketSelectControl}>
            <Select
              value={selectedTicket.toString()}
              onChange={handleTicketChange}
              displayEmpty
              IconComponent={CustomDropDownIcon}
              renderValue={(selected) => selected || "0"}
              sx={styles.selectInputTicket}
            >
              {quantityOptions.map((qty) => (
                <MenuItem key={qty} value={qty.toString()}>
                  {qty}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormControl>
        {price_description && (
          <Typography sx={styles.priceDescriptionText(theme)}>
            {price_description}
          </Typography>
        )}
      </Box>

      {/* Calendar Modal */}
      <Dialog
        open={isCalendarVisible}
        onClose={() => setIsCalendarVisible(false)}
        sx={styles.calendarDialog}
      >
        <Box sx={{ position: "relative" }}>
          {isLoadingAvailabilities && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 1,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
          <DateCalendar
            value={selectedDate}
            onChange={handleDateChange}
            onMonthChange={onMonthChange}
            slots={{
              day: CustomDay,
            }}
            views={["day"]}
            sx={styles.dateCalendar}
            minDate={dayjs()}
            disablePast
          />
        </Box>
      </Dialog>
    </LocalizationProvider>
  );
};

export default DateAndTime;
